import React, { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserCog, Plus } from "lucide-react";
import { AdminPageScaffold } from "@/components/core/AdminPageScaffold";
import { DataViewScaffold } from "@/components/core/DataViewScaffold";
import { StandardPageHeader } from "@/components/layout/StandardPageHeader";
import { SearchInput } from "@/components/ui/SearchInput";
import { StaffMember, getStaffMembers, updateStaffMember } from "@/services/staffService";
import { useSearch } from "@/hooks/useSearch";
import { useLocationStaff } from "@/hooks/useLocationStaff";
import { useStaffClassAssignments } from "@/hooks/useStaffClassAssignments";
import { useStaffLocations } from "@/hooks/useStaffLocations";
import { StaffInvitationService } from "@/services/staffInvitationService";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { useStaffLimits } from "@/hooks/useStaffLimits";
import { fetchOrganizationData } from "@/utils/auth/organizationData";
import StaffCard from "@/components/staff/StaffCard";
import StaffTableRow from "@/components/staff/StaffTableRow";
import AddStaffDialog from "@/components/staff/AddStaffDialog";
import EditStaffDialog from "@/components/staff/dialogs/EditStaffDialog";
import LocationAssignmentDialog from "@/components/staff/LocationAssignmentDialog";
import StaffLocationFilter from "@/components/staff/StaffLocationFilter";

type FilterType = "all" | "admin" | "instructor" | "manager" | "active" | "inactive";

const Staff: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, organizationId } = useAuth();
  const { isHiveShareOnly, loading: subscriptionLoading } = useSubscriptionAccess();
  
  // HiveShare user limits (only runs for HiveShare users)  
  const { data: staffLimits, isLoading: limitsLoading } = useStaffLimits();
  
  // Search
  const { searchQuery, debouncedSearchQuery, setSearchQuery, clearSearch, hasSearch } = useSearch();
  
  // State
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);

  // Data fetching
  const { data: locationStaff = [], isLoading: isLocationStaffLoading } = useLocationStaff(
    locationFilter !== "all" ? locationFilter : ""
  );
  
  const { data: allStaffMembers = [], isLoading: isAllStaffLoading, isError } = useQuery({
    queryKey: ['staffMembers'],
    queryFn: getStaffMembers,
    enabled: locationFilter === "all",
  });

  const handleStaffAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['staffMembers'] });
    if (locationFilter !== "all") {
      queryClient.invalidateQueries({ queryKey: ['locationStaff', locationFilter] });
    }
  };

  // Use the appropriate staff list based on filter
  const staffMembers = locationFilter !== "all" ? locationStaff : allStaffMembers;
  const baseLoading = locationFilter !== "all" ? isLocationStaffLoading : isAllStaffLoading;
  const isLoading = baseLoading || (isHiveShareOnly && limitsLoading);

  // User limit logic - database validation for HiveShare, existing logic for full Hivetivity
  const HIVESHARE_USER_LIMIT = 5;
  
  // For HiveShare: use database validation (rock solid)
  // For Full Hivetivity: use existing logic (unlimited)
  const currentUserCount = isHiveShareOnly && staffLimits 
    ? staffLimits.currentCount 
    : staffMembers.length;
    
  const canAddMoreUsers = isHiveShareOnly 
    ? (staffLimits?.canAddMore ?? false) // Database validation for HiveShare
    : true; // Always unlimited for full Hivetivity


  // Apply filters with memoization for performance
  const filteredStaff = useMemo(() => {
    let filtered = staffMembers;

    // Apply search filter using debounced query
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(staff => {
        const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
        const role = staff.role?.toLowerCase() || '';
        const email = staff.email?.toLowerCase() || '';
        
        return fullName.includes(query) || role.includes(query) || email.includes(query);
      });
    }

    // Apply type filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(staff => {
        switch (activeFilter) {
          case 'active':
            return Boolean(staff.is_active);
          case 'inactive':
            return !staff.is_active;
          case 'admin':
          case 'instructor':
          case 'manager':
            return staff.role === activeFilter;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [staffMembers, debouncedSearchQuery, activeFilter]);

  // Stable render functions
  const renderStaffCard = useCallback((staff: StaffMember) => {
    return <StaffCardWithData key={staff.id} staff={staff} />;
  }, []);

  const renderStaffTableRow = useCallback((staff: StaffMember) => {
    return <StaffTableRowWithData key={staff.id} staff={staff} />;
  }, []);

  // Event handlers
  const handleStatusChange = async (staffId: string, isActive: boolean) => {
    try {
      const staff = staffMembers.find(s => s.id === staffId);
      if (!staff) {
        toast.error("Staff member not found");
        return;
      }

      await updateStaffMember(staffId, { ...staff, is_active: isActive });
      toast.success(`${staff.first_name || 'Staff member'} ${staff.last_name || ''} is now ${isActive ? 'active' : 'inactive'}`);
      handleStaffAdded();
    } catch (error) {
      console.error("Error updating staff status:", error);
      toast.error("Failed to update staff status");
    }
  };

  const handleEdit = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setIsEditDialogOpen(true);
  };

  const handleInviteWithoutEmail = (staff: StaffMember) => {
    toast.info("Please add an email address first", {
      description: "You need to add an email address before sending an invite"
    });
    handleEdit(staff);
  };

  const handleSendInvite = async (staff: StaffMember) => {
    if (!staff.email || !organizationId) {
      toast.error("Cannot send invite: Missing email or organization");
      return;
    }

    try {
      let organizationName = undefined;
      try {
        const orgData = await fetchOrganizationData(organizationId);
        organizationName = orgData?.name;
      } catch (orgErr) {
        console.warn('Could not fetch organization name for invite:', orgErr);
      }

      const result = await StaffInvitationService.sendStaffInvite(staff.email, {
        staff_id: staff.id,
        staffName: `${staff.first_name || 'Unknown'} ${staff.last_name || 'User'}`,
        role: staff.role || 'staff',
        organization_id: organizationId,
        organizationName,
        firstName: staff.first_name || 'Unknown',
        lastName: staff.last_name || 'User',
        created_by: user?.id
      });

      if (result.success) {
        // Check if this was a resend (staff already had email)
        const isResend = staff.email;
        toast.success(isResend ? "Invite resent successfully" : "Invite sent successfully", {
          description: `An invite has been ${isResend ? 'resent' : 'sent'} to ${staff.email}`
        });
        handleStaffAdded();
      } else {
        toast.error("Failed to send invite", {
          description: result.error || "There was an error sending the invite"
        });
      }
    } catch (error) {
      console.error("Error sending staff invite:", error);
      toast.error("Failed to send invite");
    }
  };

  const handleManageLocations = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setIsLocationDialogOpen(true);
  };

  // Enhanced render functions
  const StaffCardWithData: React.FC<{ staff: StaffMember }> = ({ staff }) => {
    const { data: classAssignments = [] } = useStaffClassAssignments(staff.id);
    const { assignments = [] } = useStaffLocations(staff.id);
    const assignedLocationsCount = assignments.filter(a => a.assigned).length;

    return (
      <StaffCard
        staff={staff}
        onUpdate={handleStaffAdded}
      />
    );
  };

  const StaffTableRowWithData: React.FC<{ staff: StaffMember }> = ({ staff }) => {
    const { data: classAssignments = [] } = useStaffClassAssignments(staff.id);
    const { assignments = [] } = useStaffLocations(staff.id);
    const assignedLocationsCount = assignments.filter(a => a.assigned).length;

    return (
      <StaffTableRow
        staff={staff}
        classAssignments={classAssignments.length}
        assignedLocationsCount={assignedLocationsCount}
        onUpdate={handleStaffAdded}
        onStatusChange={handleStatusChange}
        onEdit={handleEdit}
        onSendInvite={handleSendInvite}
        onInviteWithoutEmail={handleInviteWithoutEmail}
        onManageLocations={handleManageLocations}
      />
    );
  };

  const renderEmptyState = () => {
    if (hasSearch || activeFilter !== "all") {
      return (
        <div className="text-center py-12">
          <UserCog className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No staff members found</h3>
          <p className="text-muted-foreground mb-4">
            {hasSearch 
              ? `No staff found matching "${searchQuery}"`
              : "Try adjusting your filters"
            }
          </p>
          {hasSearch && (
            <button 
              onClick={clearSearch}
              className="text-primary hover:text-primary/80 text-sm"
            >
              Clear search
            </button>
          )}
        </div>
      );
    }
    
    if (locationFilter !== "all") {
      return (
        <div className="text-center py-12">
          <UserCog className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No staff assigned to this location</h3>
          <p className="text-muted-foreground mb-4">
            Assign staff to this location or select a different location
          </p>
        </div>
      );
    }
    
    return (
      <div className="text-center py-12">
        <UserCog className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No staff members yet</h3>
        <p className="text-muted-foreground mb-4">
          Add your first staff member to get started
        </p>
        <AddStaffDialog 
          onStaffAdded={handleStaffAdded} 
          setEditingStaff={setSelectedStaff}
          setIsEditStaffDialogOpen={setIsEditDialogOpen}
        />
      </div>
    );
  };

  return (
    <>
      <StandardPageHeader
        title={isHiveShareOnly ? "Team Management" : "Staff Management"}
        subtitle={
          isHiveShareOnly 
            ? `Manage your team members - ${currentUserCount} of ${HIVESHARE_USER_LIMIT} users${!canAddMoreUsers ? ' (upgrade for unlimited users)' : ''}`
            : "Manage your activity center's staff members, assign roles, and track location assignments"
        }
        icon={UserCog}
        actions={
          <div className="flex items-center gap-3">
            {!isHiveShareOnly && <StaffLocationFilter value={locationFilter} onChange={setLocationFilter} />}
            <AddStaffDialog 
              onStaffAdded={handleStaffAdded} 
              setEditingStaff={setSelectedStaff}
              setIsEditStaffDialogOpen={setIsEditDialogOpen}
              disabled={!canAddMoreUsers}
              upgradeMessage={!canAddMoreUsers ? "You've reached the 5 user limit for HiveShare. Upgrade to add unlimited users." : undefined}
            />
          </div>
        }
      />
      
      <AdminPageScaffold
        loading={isLoading}
        error={isError ? new Error("Failed to load staff members") : null}
        isEmpty={staffMembers.length === 0}
        emptyState={renderEmptyState()}
      >
      <div className="space-y-6">
        {/* Data View */}
        <DataViewScaffold
          items={filteredStaff}
          views={["card", "table"]}
          defaultView="card"
          renderCard={renderStaffCard}
          renderTableRow={renderStaffTableRow}
          cardColumns={{ sm: 1, md: 2, lg: 3, xl: 3 }}
          cardGap={8}
          emptyState={renderEmptyState()}
          loading={isLoading}
        />
      </div>

      {/* Dialogs */}
      {selectedStaff && (
        <>
          <EditStaffDialog
            staff={selectedStaff}
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            onUpdate={handleStaffAdded}
          />
          
          <LocationAssignmentDialog
            staffId={selectedStaff.id}
            staffName={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
            open={isLocationDialogOpen}
            onOpenChange={setIsLocationDialogOpen}
          />
        </>
      )}
    </AdminPageScaffold>
    </>
  );
};

export default Staff;
